import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  Send, BookOpen, ScrollText, ShoppingCart, FileText, DollarSign,
  BookMarked, Scale, Download, Plus, Trash2, RefreshCw, X, AlertCircle,
  TrendingUp, MessageCircle,
} from 'lucide-react';
import { reportsAPI, posAPI, purchasesAPI } from '@/lib/api';
import { toast } from 'sonner';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${new Date().toISOString().slice(0, 7)}-01`;
const nDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); };
const yearStart = () => `${new Date().getFullYear()}-01-01`;
const thisMonth = () => new Date().toISOString().slice(0, 7); // YYYY-MM

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const header = Object.keys(rows[0]).join(',');
  const body = rows.map(r => Object.values(r).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

const PERIOD_PRESETS = [
  { label: 'Today',       start: today,    end: today },
  { label: 'This Week',   start: weekStart, end: today },
  { label: 'This Month',  start: monthStart, end: today },
  { label: 'Last 90 Days', start: () => nDaysAgo(90), end: today },
  { label: 'This Year',   start: yearStart, end: today },
  { label: 'Custom',      start: null, end: null },
];

function DateRow({ startDate, endDate, setStartDate, setEndDate }) {
  return (
    <div className="flex items-center gap-4 text-sm border border-border rounded-xl p-4 bg-card">
      <label className="flex items-center gap-2 text-muted-foreground">
        From <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          className="border border-border rounded-md px-2 py-1 text-foreground" />
      </label>
      <label className="flex items-center gap-2 text-muted-foreground">
        To: <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          className="border border-border rounded-md px-2 py-1 text-foreground" />
      </label>
    </div>
  );
}

// ── Send to CA Modal ──────────────────────────────────────────────────────────
function SendToCAModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Send Reports to CA</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex flex-col items-center py-6 space-y-4 text-center">
          <div className="relative flex items-center justify-center">
            <div className="absolute w-20 h-20 rounded-full bg-emerald-100 animate-ping opacity-30" />
            <div className="relative w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <MessageCircle className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <div>
            <p className="text-base font-bold text-foreground">WhatsApp Integration Coming Soon</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-xs">
              You'll be able to send your financial reports directly to your CA's WhatsApp with one tap.
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
            We're working on it
          </div>
        </div>

        <button onClick={onClose} className="w-full border border-border rounded-xl py-2.5 text-sm font-medium hover:bg-muted transition-colors">
          Close
        </button>
      </div>
    </div>
  );
}

// ── Profit & Loss Tab ─────────────────────────────────────────────────────────
function ProfitLossTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getProfitLoss(startDate, endDate);
      setData(res.data);
    } catch { toast.error('Failed to load P&L data'); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const chartData = [
    { name: 'Revenue',      value: data?.total_revenue || 0,  fill: '#16a34a' },
    { name: 'COGS',         value: data?.cogs || 0,           fill: '#f97316' },
    { name: 'Gross Profit', value: data?.gross_profit || 0,   fill: '#2563eb' },
    { name: 'Expenses',     value: data?.total_expenses || 0, fill: '#dc2626' },
    { name: 'Net Profit',   value: data?.net_profit || 0,     fill: data?.net_profit >= 0 ? '#16a34a' : '#dc2626' },
  ];

  return (
    <div className="space-y-5">
      <DateRow startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      {loading ? <div className="h-40 bg-muted rounded-xl animate-pulse" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-emerald-600">{fmt(data?.total_revenue)}</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground mb-1">Cost of Goods</p>
              <p className="text-2xl font-bold text-foreground">{fmt(data?.cogs)}</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Expenses</p>
              <p className="text-2xl font-bold text-red-500">{fmt(data?.total_expenses)}</p>
            </div>
            <div className={`rounded-xl border p-4 ${data?.net_profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className="text-xs text-muted-foreground mb-1">Net Profit</p>
              <p className={`text-2xl font-bold ${data?.net_profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(data?.net_profit)}</p>
              <p className="text-xs text-muted-foreground">Margin: {data?.margin_pct?.toFixed(2)}%</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold mb-4">Expense Breakdown</h3>
              {(data?.expense_breakdown || []).length === 0 ? (
                <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">No expenses recorded</div>
              ) : (
                <div className="space-y-2">
                  {data.expense_breakdown.map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{e.category}</span>
                      <span className="font-medium">{fmt(e.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold mb-4">Profit Analysis</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <RechartTooltip formatter={v => fmt(v)} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Day Book Tab ──────────────────────────────────────────────────────────────
function DayBookTab() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getDayBook(date);
      setData(res.data);
    } catch { toast.error('Failed to load day book'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const typeBadge = (type) => {
    const map = { Sale: 'bg-emerald-100 text-emerald-700', Purchase: 'bg-blue-100 text-blue-700', Expense: 'bg-red-100 text-red-700' };
    return map[type] || 'bg-muted text-muted-foreground';
  };

  const handleExport = () => {
    if (!data?.entries?.length) return toast.info('No data to export');
    downloadCSV(data.entries, `day_book_${date}.csv`);
  };

  return (
    <div className="space-y-5">
      <div className="bg-card rounded-xl border border-border p-4 flex flex-wrap items-center gap-6">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div className="ml-auto flex gap-6 text-right">
          <div>
            <p className="text-xs text-muted-foreground">Total Inflow</p>
            <p className="text-xl font-bold text-emerald-600">{fmt(data?.total_inflow)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Outflow</p>
            <p className="text-xl font-bold text-red-500">{fmt(data?.total_outflow)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Net Cash</p>
            <p className={`text-xl font-bold ${(data?.net_cash || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(data?.net_cash)}</p>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-semibold">Transactions</h3>
          <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div> : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[540px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                {['Time', 'Type', 'Particulars', 'Mode', 'Debit', 'Credit'].map(h => (
                  <th key={h} className={`py-2.5 px-4 text-muted-foreground font-medium ${h === 'Debit' || h === 'Credit' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.entries || []).map((e, i) => (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="py-2.5 px-4 text-muted-foreground">{e.time}</td>
                  <td className="py-2.5 px-4"><span className={`px-2 py-0.5 rounded text-xs font-medium ${typeBadge(e.type)}`}>{e.type}</span></td>
                  <td className="py-2.5 px-4">{e.particulars}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{e.mode}</td>
                  <td className="py-2.5 px-4 text-right text-red-500">{e.debit > 0 ? fmt(e.debit) : '—'}</td>
                  <td className="py-2.5 px-4 text-right text-emerald-600">{e.credit > 0 ? fmt(e.credit) : '—'}</td>
                </tr>
              ))}
              {(data?.entries || []).length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No transactions for this date</td></tr>
              )}
            </tbody>
            {(data?.entries || []).length > 0 && (
              <tfoot className="border-t-2 border-border font-semibold bg-muted/20">
                <tr>
                  <td colSpan={4} className="py-2.5 px-4">Total ({data.entries.length} entries)</td>
                  <td className="py-2.5 px-4 text-right text-red-500">{fmt(data?.total_outflow)}</td>
                  <td className="py-2.5 px-4 text-right text-emerald-600">{fmt(data?.total_inflow)}</td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sales Register Tab ────────────────────────────────────────────────────────
function SalesRegisterTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await posAPI.listSales({ start_date: startDate, end_date: endDate, limit: 200 });
      const items = res.data?.sales || [];
      setSales(items);
      setTotal(items.reduce((a, s) => a + (s.total_amount || 0), 0));
    } catch { toast.error('Failed to load sales'); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!sales.length) return toast.info('No data to export');
    downloadCSV(sales.map(s => ({
      Bill_Number: s.bill_number, Date: s.created_at?.slice(0, 10),
      Customer: s.customer_name || 'Consumer', Payment_Mode: s.payment_mode,
      Items: s.items?.length || 0, Total: s.total_amount, GST: s.total_gst,
    })), 'sales_register.csv');
  };

  return (
    <div className="space-y-5">
      <DateRow startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-semibold">Sales Register — {fmt(total)}</h3>
          <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div> : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[580px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                {['Date', 'Bill #', 'Customer', 'Items', 'Mode', 'GST', 'Total'].map(h => (
                  <th key={h} className={`py-2.5 px-4 text-muted-foreground font-medium ${['GST','Total'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sales.map((s, i) => (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="py-2.5 px-4 text-muted-foreground">{s.created_at?.slice(0, 10)}</td>
                  <td className="py-2.5 px-4 font-medium">{s.bill_number}</td>
                  <td className="py-2.5 px-4">{s.customer_name || 'Consumer'}</td>
                  <td className="py-2.5 px-4">{s.items?.length || 0}</td>
                  <td className="py-2.5 px-4 capitalize text-muted-foreground">{s.payment_mode}</td>
                  <td className="py-2.5 px-4 text-right text-amber-600">{fmt(s.total_gst)}</td>
                  <td className="py-2.5 px-4 text-right font-semibold text-emerald-600">{fmt(s.total_amount)}</td>
                </tr>
              ))}
              {!sales.length && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No sales for this period</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Purchase Register Tab ─────────────────────────────────────────────────────
function PurchaseRegisterTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [purchases, setPurchases] = useState([]);
  const [totalAmt, setTotalAmt] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchasesAPI.getAll({ start_date: startDate, end_date: endDate, limit: 200 });
      const items = res.data?.items || [];
      setPurchases(items);
      setTotalAmt(items.reduce((a, p) => a + (p.total_amount || 0), 0));
    } catch { toast.error('Failed to load purchases'); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!purchases.length) return toast.info('No data to export');
    downloadCSV(purchases.map(p => ({
      Date: p.purchase_date, Bill_Number: p.bill_number || '',
      Supplier: p.supplier_name, Items: p.items?.length || 0,
      Payment_Mode: p.payment_mode, Total: p.total_amount,
    })), 'purchase_register.csv');
  };

  return (
    <div className="space-y-5">
      <DateRow startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-semibold">Purchase Register — {fmt(totalAmt)}</h3>
          <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div> : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                {['Date', 'Bill #', 'Supplier', 'Items', 'Mode', 'Total'].map(h => (
                  <th key={h} className={`py-2.5 px-4 text-muted-foreground font-medium ${h === 'Total' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {purchases.map((p, i) => (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="py-2.5 px-4 text-muted-foreground">{p.purchase_date}</td>
                  <td className="py-2.5 px-4 font-medium">{p.bill_number || '—'}</td>
                  <td className="py-2.5 px-4">{p.supplier_name || '—'}</td>
                  <td className="py-2.5 px-4">{p.items?.length || 0}</td>
                  <td className="py-2.5 px-4 capitalize text-muted-foreground">{p.payment_mode || '—'}</td>
                  <td className="py-2.5 px-4 text-right font-semibold">{fmt(p.total_amount)}</td>
                </tr>
              ))}
              {!purchases.length && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No purchases for this period</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── GST Returns Tab ───────────────────────────────────────────────────────────
function GSTReturnsTab() {
  const [month, setMonth] = useState(thisMonth());
  const [subTab, setSubTab] = useState('gstr3b');
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, invRes] = await Promise.all([
        reportsAPI.getGstReturns(month),
        reportsAPI.getGstInvoices(month),
      ]);
      setSummary(sumRes.data);
      setInvoices(invRes.data?.invoices || []);
    } catch { toast.error('Failed to load GST returns'); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (subTab === 'gstr3b') {
      if (!summary?.rows?.length) return toast.info('No data to export');
      downloadCSV(summary.rows, `gstr3b_${month}.csv`);
    } else {
      if (!invoices.length) return toast.info('No data to export');
      downloadCSV(invoices, `gstr1_${month}.csv`);
    }
  };

  return (
    <div className="space-y-5">
      {/* GSTIN warning */}
      {summary && !summary.gstin && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Your GSTIN is not set. Add it in <strong className="ml-1 cursor-pointer underline">Settings → Profile</strong> for complete return filing.
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">GST Returns</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Return Period</span>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm" />
        </div>
      </div>

      <p className="text-sm text-muted-foreground">GSTR-1 (invoice-wise) &amp; GSTR-3B (summary) — export-ready for CA filing</p>

      {/* Sub tabs */}
      <div className="flex gap-2">
        {[{ id: 'gstr3b', label: 'GSTR-3B (Summary)' }, { id: 'gstr1', label: 'GSTR-1 (Invoice-wise)' }].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              subTab === t.id ? 'bg-card border-border text-foreground' : 'bg-transparent border-transparent text-muted-foreground hover:text-foreground'
            }`}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="h-40 bg-muted rounded-xl animate-pulse" /> : (
        <>
          {subTab === 'gstr3b' && summary && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total Taxable Value', val: fmt(summary.total_taxable_value) },
                  { label: 'Total CGST',          val: fmt(summary.total_cgst), color: 'text-blue-600' },
                  { label: 'Total SGST',          val: fmt(summary.total_sgst), color: 'text-blue-600' },
                  { label: 'Total GST Liability', val: fmt(summary.total_gst_liability), color: 'text-amber-600' },
                ].map(c => (
                  <div key={c.label} className="bg-card rounded-xl border border-border p-4">
                    <p className="text-xs text-muted-foreground mb-1">{c.label}</p>
                    <p className={`text-xl font-bold ${c.color || 'text-foreground'}`}>{c.val}</p>
                  </div>
                ))}
              </div>
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                  <span className="text-sm text-muted-foreground">Rate-wise outward supply summary · {summary.total_invoices} invoices</span>
                  <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                    <Download className="h-4 w-4" /> Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[500px] text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      {['GST Rate', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Tax'].map(h => (
                        <th key={h} className={`py-2.5 px-4 text-muted-foreground font-medium ${h === 'GST Rate' ? 'text-left' : 'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.rows || []).map((r, i) => (
                      <tr key={i} className={`border-t border-border/50 ${r.gst_rate === 18 ? 'font-semibold' : ''}`}>
                        <td className="py-2.5 px-4">{r.gst_rate}%</td>
                        <td className="py-2.5 px-4 text-right">{fmt(r.taxable_value)}</td>
                        <td className="py-2.5 px-4 text-right text-blue-600">{fmt(r.cgst)}</td>
                        <td className="py-2.5 px-4 text-right text-blue-600">{fmt(r.sgst)}</td>
                        <td className="py-2.5 px-4 text-right text-muted-foreground">{fmt(r.igst)}</td>
                        <td className="py-2.5 px-4 text-right text-amber-600">{fmt(r.total_tax)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-border font-bold bg-muted/20">
                      <td className="py-2.5 px-4">Total</td>
                      <td className="py-2.5 px-4 text-right">{fmt(summary.total_taxable_value)}</td>
                      <td className="py-2.5 px-4 text-right text-blue-600">{fmt(summary.total_cgst)}</td>
                      <td className="py-2.5 px-4 text-right text-blue-600">{fmt(summary.total_sgst)}</td>
                      <td className="py-2.5 px-4 text-right">—</td>
                      <td className="py-2.5 px-4 text-right text-amber-600">{fmt(summary.total_gst_liability)}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {subTab === 'gstr1' && (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <span className="text-sm text-muted-foreground">{invoices.length} invoices</span>
                <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                  <Download className="h-4 w-4" /> Export CSV
                </button>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    {['Invoice #', 'Date', 'Customer', 'Taxable Value', 'Total GST', 'Total'].map(h => (
                      <th key={h} className={`py-2.5 px-4 text-muted-foreground font-medium ${['Taxable Value','Total GST','Total'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => (
                    <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                      <td className="py-2.5 px-4 font-medium">{inv.bill_number}</td>
                      <td className="py-2.5 px-4 text-muted-foreground">{inv.date}</td>
                      <td className="py-2.5 px-4">{inv.customer}</td>
                      <td className="py-2.5 px-4 text-right">{fmt(inv.taxable_value)}</td>
                      <td className="py-2.5 px-4 text-right text-amber-600">{fmt(inv.total_gst)}</td>
                      <td className="py-2.5 px-4 text-right font-semibold">{fmt(inv.total_amount)}</td>
                    </tr>
                  ))}
                  {!invoices.length && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No invoices for this month</td></tr>}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Expenses Tab ──────────────────────────────────────────────────────────────
function ExpensesTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [expenses, setExpenses] = useState([]);
  const [totalExp, setTotalExp] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: today(), category: '', amount: '', description: '' });
  const [saving, setSaving] = useState(false);

  const CATEGORIES = ['Rent', 'Electricity', 'Salary', 'Transport', 'Packaging', 'Marketing', 'Maintenance', 'Misc'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getExpenses(startDate, endDate);
      setExpenses(res.data.items || []);
      setTotalExp(res.data.total || 0);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.category || !form.amount) return toast.error('Category and amount are required');
    setSaving(true);
    try {
      await reportsAPI.addExpense({ ...form, amount: parseFloat(form.amount) });
      setShowAdd(false);
      setForm({ date: today(), category: '', amount: '', description: '' });
      toast.success('Expense added');
      load();
    } catch { toast.error('Failed to save expense'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await reportsAPI.deleteExpense(id);
      toast.success('Expense deleted');
      load();
    } catch { toast.error('Failed to delete expense'); }
  };

  const handleExport = () => {
    if (!expenses.length) return toast.info('No data to export');
    downloadCSV(expenses, `expenses_${startDate}_${endDate}.csv`);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4 flex-wrap">
        <DateRow startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        <div className="flex items-center gap-3 ml-auto">
          <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-2">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 text-sm bg-primary text-primary-foreground rounded-lg px-3 py-2 hover:bg-primary/90">
            <Plus className="h-4 w-4" /> Add Expense
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 bg-card rounded-xl border border-border p-4">
        <div>
          <p className="text-xs text-muted-foreground">Total Expenses</p>
          <p className="text-2xl font-bold text-red-500">{fmt(totalExp)}</p>
        </div>
      </div>

      {/* Add Expense Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold">Add Expense</h2>
              <button onClick={() => setShowAdd(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            {[
              { label: 'Date', type: 'date', field: 'date' },
              { label: 'Amount (₹)', type: 'number', field: 'amount', placeholder: '0.00' },
              { label: 'Description', type: 'text', field: 'description', placeholder: 'Optional' },
            ].map(f => (
              <div key={f.field}>
                <label className="block text-sm font-medium mb-1">{f.label}</label>
                <input type={f.type} value={form[f.field]} placeholder={f.placeholder}
                  onChange={e => setForm(p => ({ ...p, [f.field]: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setShowAdd(false)} className="flex-1 border border-border rounded-xl py-2.5 text-sm">Cancel</button>
              <button onClick={handleAdd} disabled={saving}
                className="flex-1 bg-primary text-primary-foreground rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">
                {saving ? 'Saving…' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div> : (
          expenses.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-4 text-muted-foreground">
              <DollarSign className="h-10 w-10 opacity-30" />
              <p>No expenses recorded for this period.</p>
              <button onClick={() => setShowAdd(true)}
                className="flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-medium">
                <Plus className="h-4 w-4" /> Add First Expense
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[440px] text-sm">
              <thead className="bg-muted/40">
                <tr>
                  {['Date', 'Category', 'Description', 'Amount', ''].map((h, i) => (
                    <th key={i} className={`py-2.5 px-4 text-muted-foreground font-medium ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenses.map((e, i) => (
                  <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                    <td className="py-2.5 px-4 text-muted-foreground">{e.date}</td>
                    <td className="py-2.5 px-4">
                      <span className="bg-muted px-2 py-0.5 rounded text-xs font-medium">{e.category}</span>
                    </td>
                    <td className="py-2.5 px-4 text-muted-foreground">{e.description || '—'}</td>
                    <td className="py-2.5 px-4 text-right font-semibold text-red-500">{fmt(e.amount)}</td>
                    <td className="py-2.5 px-4 text-right">
                      <button onClick={() => handleDelete(e.id)} className="text-muted-foreground hover:text-red-500 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ── Journal Ledger Tab ────────────────────────────────────────────────────────
function JournalLedgerTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [account, setAccount] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const ACCOUNTS = ['all', 'Cash', 'Sales Revenue', 'Purchases', 'Trade Receivables', 'GST Payable'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getJournalLedger(startDate, endDate, account);
      setData(res.data);
    } catch { toast.error('Failed to load journal ledger'); }
    finally { setLoading(false); }
  }, [startDate, endDate, account]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleExport = () => {
    if (!data?.entries?.length) return toast.info('No data to export');
    downloadCSV(data.entries, `journal_ledger_${startDate}_${endDate}.csv`);
  };

  const sourceBadge = (s) => {
    const map = { Sale: 'bg-emerald-100 text-emerald-700', Purchase: 'bg-blue-100 text-blue-700', Expense: 'bg-red-100 text-red-700' };
    return map[s] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-5">
      <div className="bg-card rounded-xl border border-border p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">From</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">To</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Account</label>
          <select value={account} onChange={e => setAccount(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm min-w-[160px]">
            {ACCOUNTS.map(a => <option key={a} value={a}>{a === 'all' ? 'All Accounts' : a}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-4 py-1.5 text-sm font-medium">
            Apply
          </button>
          <button onClick={handleExport} className="flex items-center gap-1.5 text-sm border border-border rounded-lg px-3 py-1.5 text-muted-foreground hover:text-foreground">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={load} className="flex items-center gap-1.5 text-sm border border-border rounded-lg px-3 py-1.5 text-muted-foreground hover:text-foreground">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card rounded-xl border border-border p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Entries</p>
            <p className="text-2xl font-bold">{data.total_entries}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Debits</p>
            <p className="text-2xl font-bold text-emerald-600">{fmt(data.total_debits)}</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Credits</p>
            <p className="text-2xl font-bold text-red-500">{fmt(data.total_credits)}</p>
          </div>
        </div>
      )}

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
          <BookMarked className="h-4 w-4 text-violet-600" />
          <h3 className="font-semibold">Journal Ledger</h3>
          <p className="text-xs text-muted-foreground ml-1">Auto-posted double-entry from all transactions</p>
        </div>
        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div> : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                {['DATE', 'REF', 'NARRATION', 'ACCOUNT', 'DR/CR', 'AMOUNT (₹)', 'SOURCE'].map(h => (
                  <th key={h} className={`py-2.5 px-4 text-xs text-muted-foreground font-medium ${h === 'AMOUNT (₹)' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.entries || []).map((e, i) => (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="py-2.5 px-4 text-muted-foreground">{e.date}</td>
                  <td className="py-2.5 px-4 font-mono text-xs">{e.ref}</td>
                  <td className="py-2.5 px-4">{e.narration}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{e.account}</td>
                  <td className="py-2.5 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${e.dr_cr === 'Dr' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>{e.dr_cr}</span>
                  </td>
                  <td className="py-2.5 px-4 text-right font-medium">{fmt(e.amount)}</td>
                  <td className="py-2.5 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs ${sourceBadge(e.source)}`}>{e.source}</span>
                  </td>
                </tr>
              ))}
              {!(data?.entries?.length) && (
                <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No entries for this period</td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Balance Sheet Tab ─────────────────────────────────────────────────────────
function BalanceSheetTab() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getBalanceSheet(asOf);
      setData(res.data);
    } catch { toast.error('Failed to load balance sheet'); }
    finally { setLoading(false); }
  }, [asOf]);

  useEffect(() => { load(); }, [load]);

  const hasDiff = data && Math.abs(data.difference) > 0.5;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">As of Date</label>
          <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
            className="border border-border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="mt-5 flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {hasDiff && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <Scale className="h-4 w-4 flex-shrink-0" />
          ⚠ Difference of {fmt(Math.abs(data.difference))} — estimated figures
        </div>
      )}

      {loading ? <div className="h-64 bg-muted rounded-xl animate-pulse" /> : data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Assets */}
          <div className="bg-card rounded-xl border border-border p-5 space-y-3">
            <h3 className="font-bold flex items-center gap-2 text-emerald-700">
              <TrendingUp className="h-4 w-4" /> Assets
            </h3>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Inventory (at cost)',       val: data.assets.inventory_at_cost },
                { label: 'Trade Receivables (Khata)', val: data.assets.trade_receivables },
                { label: 'Cash & Bank (est.)',         val: data.assets.cash_bank },
              ].map(r => (
                <div key={r.label} className="flex items-center justify-between py-1 border-b border-border/40">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className="font-medium text-emerald-600">{fmt(r.val)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1 font-bold">
                <span>Total Assets</span>
                <span>{fmt(data.assets.total)}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">* Cash &amp; Bank is estimated from revenue minus payments. Inventory uses cost price × stock.</p>
          </div>

          <div className="space-y-6">
            {/* Liabilities */}
            <div className="bg-card rounded-xl border border-border p-5 space-y-3">
              <h3 className="font-bold flex items-center gap-2 text-red-600">
                <AlertCircle className="h-4 w-4" /> Liabilities
              </h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'GST Payable',      val: data.liabilities.gst_payable },
                  { label: 'Accounts Payable', val: data.liabilities.accounts_payable },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between py-1 border-b border-border/40">
                    <span className="text-muted-foreground">{r.label}</span>
                    <span className="font-medium text-red-500">{fmt(r.val)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1 font-bold">
                  <span>Total Liabilities</span>
                  <span>{fmt(data.liabilities.total)}</span>
                </div>
              </div>
            </div>

            {/* Equity */}
            <div className="bg-card rounded-xl border border-border p-5 space-y-3">
              <h3 className="font-bold flex items-center gap-2 text-blue-700">
                <Scale className="h-4 w-4" /> Equity (Net P&amp;L)
              </h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Net Revenue (ex-GST)', val: data.equity.net_revenue_ex_gst,   color: 'text-foreground' },
                  { label: 'Less: Purchases',       val: data.equity.less_purchases,       color: 'text-red-500' },
                  { label: 'Gross Profit',          val: data.equity.gross_profit,         color: 'text-foreground' },
                  { label: 'Less: Expenses',        val: data.equity.less_expenses,        color: 'text-red-500' },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between py-1 border-b border-border/40">
                    <span className="text-muted-foreground">{r.label}</span>
                    <span className={`font-medium ${r.color}`}>{fmt(r.val)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1 font-bold">
                  <span>Net Profit / (Loss)</span>
                  <span className={data.equity.net_profit >= 0 ? 'text-emerald-600' : 'text-red-500'}>{fmt(data.equity.net_profit)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="flex items-center justify-between px-5 py-3 bg-muted/40 rounded-xl font-bold text-sm">
          <span>Total Assets</span>
          <span>{fmt(data.assets.total)}</span>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'pl',        label: 'Profit & Loss',    icon: TrendingUp },
  { id: 'daybook',   label: 'Day Book',         icon: BookOpen },
  { id: 'sales',     label: 'Sales Register',   icon: ScrollText },
  { id: 'purchases', label: 'Purchase Register',icon: ShoppingCart },
  { id: 'gst',       label: 'GST Returns',      icon: FileText },
  { id: 'expenses',  label: 'Expenses',         icon: DollarSign },
  { id: 'journal',   label: 'Journal Ledger',   icon: BookMarked },
  { id: 'balance',   label: 'Balance Sheet',    icon: Scale },
];

export default function AccountingGST() {
  const [activeTab, setActiveTab] = useState('pl');
  const [showCA, setShowCA] = useState(false);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" /> Accounting &amp; GST
          </h1>
        </div>
      </div>

      {/* Send to CA Banner */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Send className="h-5 w-5 text-emerald-600" />
          <div>
            <p className="font-semibold text-emerald-800 text-sm">Send Reports to your CA</p>
            <p className="text-xs text-emerald-700">Share all financial reports (Sales, Expenses, GST summary) with your Chartered Accountant via email &amp; WhatsApp.</p>
          </div>
        </div>
        <button onClick={() => setShowCA(true)}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shrink-0">
          <Send className="h-4 w-4" /> Send to CA
        </button>
      </div>

      {/* Tab Navigation (scrollable) */}
      <div className="flex gap-1 overflow-x-auto border-b border-border pb-0 -mb-0">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border border-b-0 rounded-t-lg transition-colors -mb-px shrink-0 ${
                activeTab === t.id
                  ? 'bg-card border-border text-foreground'
                  : 'bg-muted/30 border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'pl'        && <ProfitLossTab />}
        {activeTab === 'daybook'   && <DayBookTab />}
        {activeTab === 'sales'     && <SalesRegisterTab />}
        {activeTab === 'purchases' && <PurchaseRegisterTab />}
        {activeTab === 'gst'       && <GSTReturnsTab />}
        {activeTab === 'expenses'  && <ExpensesTab />}
        {activeTab === 'journal'   && <JournalLedgerTab />}
        {activeTab === 'balance'   && <BalanceSheetTab />}
      </div>

      {showCA && <SendToCAModal onClose={() => setShowCA(false)} />}
    </div>
  );
}
